/**
 * ColumnHeaderFilter — Excel-style per-column filter affordance for the NCR
 * list. A funnel icon in the column header opens a popover with the right
 * control for the column type. The value is wired to the existing
 * server-side NCR filter state (NCRFilterSet), so filtering works across the
 * whole paginated dataset, not just the visible page.
 *
 * Phase 1 supports 'choice' (single-select from a fixed option list) and
 * 'text' (contains) columns that already have a backend filter. Multi-select
 * and dynamic distinct-value facets are deferred to Phase 2.
 */
import { useState } from 'react';
import { Filter } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export type ColumnFilterType = 'choice' | 'text';

export interface ColumnFilterOption {
  value: string;
  label: string;
}

export interface ColumnFilterDescriptor {
  type: ColumnFilterType;
  value: string;
  onChange: (value: string) => void;
  options?: ColumnFilterOption[];
  allLabel?: string;
}

interface ColumnHeaderFilterProps extends ColumnFilterDescriptor {
  /** Column label, used for accessible names / placeholders. */
  label: string;
}

export default function ColumnHeaderFilter({
  label,
  type,
  value,
  onChange,
  options = [],
  allLabel = 'All',
}: ColumnHeaderFilterProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const active = value.trim() !== '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter ${label}`}
          // Stop the header's sort handler from firing when opening the filter.
          onClick={(e) => e.stopPropagation()}
          className={`ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-black/10 ${
            active ? 'text-primary' : 'text-muted-foreground/50'
          }`}
        >
          <Filter className="h-3.5 w-3.5" fill={active ? 'currentColor' : 'none'} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-2"
        onClick={(e) => e.stopPropagation()}
      >
        {type === 'choice' ? (
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              className={`w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${
                !active ? 'font-medium text-primary' : ''
              }`}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              {allLabel}
            </button>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted ${
                  value === o.value ? 'font-medium text-primary' : ''
                }`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onChange(text.trim());
              setOpen(false);
            }}
            className="space-y-2"
          >
            <Input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Filter ${label}…`}
              className="h-8 text-sm"
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" className="h-7 px-2 text-xs">
                Apply
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setText('');
                  onChange('');
                  setOpen(false);
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
}
